import React from 'react';
import { 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  IconButton, 
  Tooltip, 
  Chip,
  Avatar
} from '@mui/material';
import ReactMarkdown from 'react-markdown';
import remarkBreaks from 'remark-breaks';
import { format } from 'date-fns';
import PushPinIcon from '@mui/icons-material/PushPin';
import PushPinOutlinedIcon from '@mui/icons-material/PushPinOutlined';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import MarkEmailReadIcon from '@mui/icons-material/MarkEmailRead';
import MarkEmailUnreadIcon from '@mui/icons-material/MarkEmailUnread';
import { AvisoComLeitura } from '../../types/avisos';
import { useAuth } from '../../contexts/AuthContext';
import { useDepartamento } from '../../contexts/DepartamentoContext';
import AnexosAviso from './AnexosAviso';

interface AvisoItemProps {
  aviso: AvisoComLeitura;
  onMarcarLido: (id: string) => void;
  onFixar: (id: string) => void;
  onDesafixar: (id: string) => void;
  onEditar?: (id: string) => void;
  onExcluir?: (id: string) => void;
  onClick?: (id: string) => void;
}

const AvisoItem: React.FC<AvisoItemProps> = ({
  aviso,
  onMarcarLido,
  onFixar,
  onDesafixar,
  onEditar,
  onExcluir,
  onClick
}) => {
  const { usuario } = useAuth();
  const { obterDepartamentoPorId } = useDepartamento();
  
  // Verificar se o usuário atual é o criador do aviso ou um admin
  const podeEditar = usuario?.id === aviso.usuario_id || usuario?.tipo === 'admin';
  
  // Formatar a data de criação
  const dataFormatada = format(
    new Date(aviso.data_criacao),
    "dd/MM/yyyy 'às' HH:mm"
  );
  
  // Determinar o tipo de destinatário para exibição
  const obterTipoDestinatario = () => {
    switch (aviso.tipo) {
      case 'geral':
        return 'Todos';
      case 'departamento':
        const departamento = aviso.departamento_id ? 
          obterDepartamentoPorId(aviso.departamento_id) : null;
        return departamento ? `Departamento: ${departamento.nome}` : 'Departamento';
      case 'usuario':
        return 'Usuários específicos';
      default:
        return 'Desconhecido';
    }
  };
  
  // Determinar a cor do chip de tipo
  const obterCorTipo = () => {
    switch (aviso.tipo) {
      case 'geral':
        return 'primary';
      case 'departamento':
        return 'success';
      case 'usuario':
        return 'info';
      default:
        return 'default';
    }
  };

  return (
    <Card
      id={`aviso-${aviso.id}`}
      sx={{
        mb: 2,
        position: 'relative',
        borderLeft: aviso.lido ? '4px solid' : '4px solid #e10600',
        borderLeftColor: aviso.lido ? 'divider' : '#e10600',
        backgroundColor: aviso.fixado ? 'rgba(255, 193, 7, 0.08)' : 'inherit',
        transition: 'box-shadow 0.2s, background-color 0.6s',
        '&:hover': {
          boxShadow: 3,
          cursor: onClick ? 'pointer' : 'default'
        }
      }}
      onClick={onClick ? () => onClick(aviso.id) : undefined}
    >
      
      <CardContent>
        {/* Cabeçalho com informações do usuário e data */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Avatar 
              src="/profilepicture.png" 
              alt={aviso.criador?.nome || 'Usuário'} 
              sx={{ width: 32, height: 32, mr: 1 }}
            />
            <Box>
              <Typography variant="subtitle2" component="span">
                {aviso.criador?.nome || 'Usuário'}
              </Typography>
              <Typography variant="caption" color="text.secondary" display="block">
                {dataFormatada}
              </Typography>
            </Box>
          </Box>
        </Box>
        
        {/* Badges e status */}
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          {aviso.fixado && (
            <Chip 
              icon={<PushPinIcon fontSize="small" />}
              label="Fixado"
              size="small"
              color="warning"
            />
          )}
          
          <Chip 
            label={obterTipoDestinatario()} 
            size="small" 
            color={obterCorTipo() as any}
          />
          
          {!aviso.lido && (
            <Chip 
              label="Não lido" 
              size="small" 
              color="primary"
              variant="outlined"
            />
          )}
        </Box>
        
        <Typography variant="h6" component="h3" gutterBottom>
          {aviso.titulo}
        </Typography>
        
        <Box 
          sx={{ 
            mt: 1, 
            mb: 1, 
            color: 'text.secondary',
            '& h1, & h2, & h3, & h4, & h5, & h6': {
              mt: 1,
              mb: 1,
              fontWeight: 'bold',
              lineHeight: 1.2
            },
            '& h1': { fontSize: '1.4rem' },
            '& h2': { fontSize: '1.3rem' },
            '& h3': { fontSize: '1.2rem' },
            '& h4': { fontSize: '1.1rem' },
            '& h5, & h6': { fontSize: '1rem' },
            '& p': { mb: 1 },
            '& ul, & ol': { pl: 2, mb: 1 },
            '& a': { color: 'primary.main', textDecoration: 'underline' },
            '& blockquote': {
              borderLeft: '3px solid',
              borderLeftColor: 'divider',
              pl: 1,
              ml: 0,
              fontStyle: 'italic'
            },
            '& code': {
              backgroundColor: 'action.hover',
              padding: '2px 4px',
              borderRadius: '3px',
              fontFamily: 'monospace'
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto'
            }
          }}
        >
          <ReactMarkdown
            remarkPlugins={[remarkBreaks]}
            components={{
              // Links abrem em nova aba com segurança
              a: ({ node, ...props }) => (
                <a 
                  {...props} 
                  target="_blank" 
                  rel="noopener noreferrer"
                />
              ),
              // Imagens responsivas
              img: ({ node, ...props }) => (
                <img 
                  {...props} 
                  style={{ maxWidth: '100%', height: 'auto' }}
                  alt={props.alt || 'Imagem'}
                />
              )
            }}
          >
            {aviso.conteudo}
          </ReactMarkdown>
        </Box>

        {/* Anexos do Aviso */}
        <Box sx={{ mt: 2 }}>
          <AnexosAviso
            avisoId={aviso.id}
            podeAdicionar={false}
            podeExcluir={false}
          />
        </Box>
        
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 1 }}>
          {!aviso.lido && (
            <Tooltip title="Marcar como lido">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  onMarcarLido(aviso.id);
                }}
              >
                <MarkEmailReadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {aviso.lido && (
            <Tooltip title="Marcar como não lido">
              <IconButton 
                size="small" 
                onClick={(e) => {
                  e.stopPropagation();
                  // Esta funcionalidade será implementada posteriormente
                }}
                disabled
              >
                <MarkEmailUnreadIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          {podeEditar && (
            <>
              {aviso.fixado ? (
                <Tooltip title="Desafixar">
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onDesafixar(aviso.id);
                    }}
                  >
                    <PushPinIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Fixar">
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onFixar(aviso.id);
                    }}
                  >
                    <PushPinOutlinedIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              
              {onEditar && (
                <Tooltip title="Editar">
                  <IconButton 
                    size="small" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onEditar(aviso.id);
                    }}
                  >
                    <EditIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
              
              {onExcluir && (
                <Tooltip title="Excluir">
                  <IconButton 
                    size="small" 
                    color="error" 
                    onClick={(e) => {
                      e.stopPropagation();
                      onExcluir(aviso.id);
                    }}
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              )}
            </>
          )}
        </Box>
      </CardContent>
    </Card>
  );
};

export default AvisoItem;
